import type { DocumentDeliveryView } from './delivery';
import type { PrintDocument } from './printRegions';
import type { ApprovalView } from './approvalGate';
import type { PrintSheetLayout, PrintSheetHtml } from '../print/templates';
import { PURCHASE_ORDER_STATUSES, documentStatusPill, type ApprovalStatusValue, type PurchaseOrderStatusValue } from './statuses';
import type { DividerPlace, DividerRow } from '../composites/itemTable';
import { t } from '../strings';
import { DEFAULT_CURRENCY_CODE, formatCurrency, formatPercent } from '../money';
import { counted } from '../composites/countedUnit';

/**
 * The Purchase Order (piece 22; decisions 0029, 0391): the pure half of the
 * rules the service enforces. Drop Ship is a fulfilment method on the one
 * record, requiring its Sales Order and locking its Ship To; the margin bypass
 * compares percentages and a line with no sell price never clears it; the
 * receipt axis moves through partial receipts; Void is the receipt axis
 * reaching Cancelled. Nothing here touches a table.
 */
export const PURCHASE_FULFILLMENT_METHODS = ['Stock', 'DropShip'] as const;
export type PurchaseFulfillmentMethodValue = (typeof PURCHASE_FULFILLMENT_METHODS)[number];
export const PURCHASE_ORDER_ORIGINS = ['Generated', 'Manual'] as const;
export type PurchaseOrderOriginValue = (typeof PURCHASE_ORDER_ORIGINS)[number];
export type PurchaseOrderFulfillmentStatusValue = 'NotStarted' | 'PartiallyReceived' | 'Received' | 'Cancelled';

// ---- The margin bypass ----

/** A line as the bypass reads it: both prices in dollars, the floor as a percentage; null is "none", never zero. */
export interface MarginLine {
  purchasePrice: number | null;
  sellPrice: number | null;
  minimumMarginPercent: number | null;
}

/** The line's own margin as a percentage of its sell price; a line with no sell price, or one at zero, has no margin to measure. */
export function lineMarginPercent(sellPrice: number | null, purchasePrice: number | null): number | null {
  if (sellPrice === null || purchasePrice === null || !(sellPrice > 0)) return null;
  return ((sellPrice - purchasePrice) / sellPrice) * 100;
}

/**
 * 🔨 A line clears when (sell − purchase) ÷ sell is at or above `Item.minimumMargin`,
 * a percentage against a percentage. A line with no sell price does not clear;
 * a free-text line, which has no Item and so no floor, does not clear either.
 */
export function lineClearsMargin(line: MarginLine): boolean {
  const margin = lineMarginPercent(line.sellPrice, line.purchasePrice);
  if (margin === null || line.minimumMarginPercent === null) return false;
  return margin + 1e-9 >= line.minimumMarginPercent;
}

/** Every line clearing its floor creates the PO Approved; any line short, or no line at all, creates it Pending Approval. */
export function minimumMarginCleared(lines: readonly MarginLine[]): boolean {
  return lines.length > 0 && lines.every(lineClearsMargin);
}

/**
 * Where a line's sell price comes from (built 2026-09-14): the linked Sales
 * Order's line for the same Item, else the Item's own `salesPrice` where it is
 * above zero. Shop supplies have neither, and that is the null the bypass fails.
 */
export function resolveSellPrice(salesOrderLinePrice: number | null | undefined, itemSalesPrice: number | null | undefined): number | null {
  if (typeof salesOrderLinePrice === 'number' && salesOrderLinePrice > 0) return salesOrderLinePrice;
  if (typeof itemSalesPrice === 'number' && itemSalesPrice > 0) return itemSalesPrice;
  return null;
}

export interface MarginWorkingLine extends MarginLine {
  label: string;
}

const money = (value: number | null, currencyCode: string): string => (value === null ? '—' : formatCurrency({ amount: Math.round(value * 100), currencyCode }));

/**
 * The bypass's working, one line per order line and a verdict (decision 0375's
 * shape): read behind a super-admin control, never printed on the page. The
 * document's own currency code where it carries one, the default where it does not.
 */
export function marginWorking(lines: readonly MarginWorkingLine[], currencyCode: string = DEFAULT_CURRENCY_CODE): string[] {
  const rows = lines.map((line) => {
    const margin = lineMarginPercent(line.sellPrice, line.purchasePrice);
    const verdict = lineClearsMargin(line) ? 'clears' : line.sellPrice === null ? 'no sell price, does not clear' : line.minimumMarginPercent === null ? 'no floor, does not clear' : 'short';
    return `${line.label}: sell ${money(line.sellPrice, currencyCode)} − buy ${money(line.purchasePrice, currencyCode)} = ${formatPercent(margin)} against a ${formatPercent(line.minimumMarginPercent)} floor → ${verdict}`;
  });
  rows.push(minimumMarginCleared(lines) ? 'Every line clears its floor: the order is created Approved' : 'A line is short or unpriced: the order routes through approval');
  return rows;
}

// ---- Drop Ship and the Ship To ----

/** 🔨 DropShip requires `salesOrderId`, enforced at save. */
export function dropShipProblem(method: PurchaseFulfillmentMethodValue, salesOrderId: string | null | undefined): string | null {
  if (method === 'DropShip' && !salesOrderId) return 'a drop-ship purchase order names the Sales Order it ships against; choose that order, or change this purchase order to Stock';
  return null;
}

/** Stock allows an editable Ship To, WEA's own receiving site; DropShip locks it to the linked Sales Order's Ship To. */
export function shipToProblem(method: PurchaseFulfillmentMethodValue, shipToSiteId: string | null | undefined, salesOrderShipToSiteId?: string | null): string | null {
  if (method === 'DropShip') {
    if (!salesOrderShipToSiteId) return 'a drop-ship purchase order needs a Sales Order whose Ship To is set; open that order, set where the goods go, then come back';
    if (shipToSiteId && shipToSiteId !== salesOrderShipToSiteId) return 'a drop-ship purchase order ships to the Sales Order’s Ship To and nowhere else';
    return null;
  }
  return shipToSiteId ? null : 'a stock purchase order names the site that receives it';
}

/** The Ship To a new order takes: the Sales Order's for DropShip, whatever the person chose for Stock. */
export function resolveShipTo(method: PurchaseFulfillmentMethodValue, chosen: string | null | undefined, salesOrderShipToSiteId: string | null | undefined): string | null {
  return method === 'DropShip' ? salesOrderShipToSiteId ?? null : chosen ?? null;
}

// ---- The receipt axis ----

export interface PurchaseLineFacts {
  id: string;
  quantity: number;
  /** On a Purchase Order line, the quantity received so far (built 2026-09-14). */
  quantityConsumed: number;
}

export interface ReceiptLine {
  lineId: string;
  quantity: number;
}

export function remainingToReceive(line: PurchaseLineFacts): number {
  return Math.max(0, line.quantity - line.quantityConsumed);
}

/**
 * 🔒 A Closed order receives nothing and confirms no shipment, refused by name
 * whatever its receipt axis reads (decision 3701; T2305). An order imported
 * Closed with nothing received reads Not started on that axis, so the axis
 * alone let a receipt through.
 */
export function closedPurchaseOrderReceiptProblem(status: PurchaseOrderStatusValue): string | null {
  return status === 'Closed' ? t('po.receive.closedRefusal') : null;
}

/** The order a receipt is read against: its method, its one linear status and its receipt axis. */
export interface ReceiptOrderFacts {
  method: PurchaseFulfillmentMethodValue;
  status: PurchaseOrderStatusValue;
  fulfillmentStatus: PurchaseOrderFulfillmentStatusValue;
}

/** What refuses a receipt before anything is written: a drop-ship order, a closed, cancelled or complete one, an unknown line, a zero, a quantity past the remainder. */
export function receiptProblem(order: ReceiptOrderFacts, lines: readonly PurchaseLineFacts[], receipts: readonly ReceiptLine[]): string | null {
  const { method, fulfillmentStatus: status } = order;
  if (method === 'DropShip') return 'a drop-ship purchase order has no Receive; it closes on a Ship Confirmation';
  const closed = closedPurchaseOrderReceiptProblem(order.status);
  if (closed) return closed;
  if (status === 'Cancelled') return 'a canceled purchase order receives nothing';
  if (status === 'Received') return 'every line of this purchase order is already received';
  if (receipts.length === 0) return 'a receipt names at least one line and a quantity; enter what arrived against the lines it arrived for';
  for (const receipt of receipts) {
    const line = lines.find((l) => l.id === receipt.lineId);
    if (!line) return 'a receipt names a line of this purchase order; reload the purchase order and receive against the lines it shows';
    if (!Number.isFinite(receipt.quantity) || receipt.quantity <= 0) return 'a received quantity is above zero';
    if (receipt.quantity > remainingToReceive(line) + 1e-9) return `line ${receipt.lineId} has ${remainingToReceive(line)} left to receive`;
  }
  return null;
}

/** The receipt axis after a receipt: Received once every line is full, PartiallyReceived once any line has moved, else NotStarted. */
export function fulfillmentAfterReceipt(lines: readonly PurchaseLineFacts[]): Exclude<PurchaseOrderFulfillmentStatusValue, 'Cancelled'> {
  if (lines.length > 0 && lines.every((l) => remainingToReceive(l) <= 1e-9)) return 'Received';
  if (lines.some((l) => l.quantityConsumed > 0)) return 'PartiallyReceived';
  return 'NotStarted';
}

/** A drop-ship order closes on a Ship Confirmation, date and tracking reference, and a Stock order has no such action; a Closed one confirms none (decision 3701). */
export function shipConfirmationProblem(order: ReceiptOrderFacts, input: { shippedOn: string | null | undefined; trackingNumber: string | null | undefined }): string | null {
  const { method, fulfillmentStatus: status } = order;
  if (method !== 'DropShip') return 'a stock purchase order is received, not ship-confirmed';
  const closed = closedPurchaseOrderReceiptProblem(order.status);
  if (closed) return closed;
  if (status === 'Cancelled') return 'a canceled purchase order confirms no shipment';
  if (status === 'Received') return 'this drop shipment is already confirmed';
  if (!input.shippedOn || !/^\d{4}-\d{2}-\d{2}$/.test(input.shippedOn)) return 'a Ship Confirmation carries the date the goods left; pick the date the goods went out';
  if (!input.trackingNumber || input.trackingNumber.trim().length === 0) return 'a Ship Confirmation carries the tracking reference; enter the carrier’s tracking number';
  return null;
}

/** 🔨 Never bill a drop-ship PO on send: it becomes billable on Ship Confirmation, a Stock order on receipt. */
export function billableOn(method: PurchaseFulfillmentMethodValue): 'Receipt' | 'ShipConfirmation' {
  return method === 'DropShip' ? 'ShipConfirmation' : 'Receipt';
}

// ---- The printed line ----

/** The three part references a purchase line carries: the distributor's, the manufacturer's part number and the manufacturer's reference name. */
export interface PurchaseLineReferenceFacts {
  vendorPartNumber: string | null;
  manufacturerPartNumber: string | null;
  manufacturerReferenceName: string | null;
}

/** What a printed purchase line carries of them, each field alone and captioned by nothing. */
export interface PurchaseLineReference {
  /** The MFR Part # column: the manufacturer's part number and nothing else. */
  mfrPart: string | null;
  /** The MFR Reference column, which a template draws only where its column set names it. */
  mfrReference: string | null;
  /** The distributor's own part number, drawn in the Item cell under the SKU. */
  vendorPart: string | null;
}

/**
 * 🔒 The printed purchase line's references, each in its own place (T117;
 * decision 0641). The product owner, 2026-09-20: *"the MFR Part Number shows
 * MFR# xxxx (aaa) that should just be the xxxx part since ther column has MFR
 * Part # as title so the text is redundant and the () part would be another
 * column if selected"*. 💰 The column held a captioned sentence, *MFR # LC1D32
 * (TeSys D)*, under a heading that already said MFR Part #.
 */
export function purchaseLineReference(facts: PurchaseLineReferenceFacts): PurchaseLineReference {
  return {
    mfrPart: facts.manufacturerPartNumber || null,
    mfrReference: facts.manufacturerReferenceName || null,
    vendorPart: facts.vendorPartNumber || null,
  };
}

/**
 * The note a purchase line reads under its name on the order's own page, which
 * draws no reference columns and so names each part it shows (piece 22).
 */
export function purchaseLineNote(facts: PurchaseLineReferenceFacts): string | null {
  const parts: string[] = [];
  if (facts.vendorPartNumber) parts.push(`Vendor # ${facts.vendorPartNumber}`);
  if (facts.manufacturerPartNumber) parts.push(facts.manufacturerReferenceName ? `MFR # ${facts.manufacturerPartNumber} (${facts.manufacturerReferenceName})` : `MFR # ${facts.manufacturerPartNumber}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ---- Void (decision 0391) ----

/** The receipt member Void writes, and the word the lifecycle service reads it back as. */
export const PURCHASE_ORDER_VOID_MEMBER: PurchaseOrderFulfillmentStatusValue = 'Cancelled';

/** The receipt axis presented as the document's lifecycle status: Cancelled reads Void, every other member reads as itself. */
export function lifecycleStatusOf(status: PurchaseOrderFulfillmentStatusValue): string {
  return status === 'Cancelled' ? 'Void' : status;
}

/** The lifecycle service's write translated back onto the receipt axis. */
export function receiptStatusOf(lifecycleStatus: string): PurchaseOrderFulfillmentStatusValue {
  if (lifecycleStatus === 'Void') return PURCHASE_ORDER_VOID_MEMBER;
  return lifecycleStatus as PurchaseOrderFulfillmentStatusValue;
}

/** Void is refused once anything is received; the receipt stands in the ledger. */
export function voidProblemOnReceipt(status: PurchaseOrderFulfillmentStatusValue): string | null {
  if (status === 'Cancelled') return 'the purchase order is already void';
  if (status !== 'NotStarted') return 'a purchase order with a receipt against it is not voided; its receipts stand in the ledger';
  return null;
}

// ---- The one status (decision 1072) ----

/**
 * The words a status reads as in a refusal: the status table's own label, the one the pill draws (T2172),
 * so a refusal and the pill beside it cannot come to name a status two ways.
 * Decision 1225: Sending and Delivery Failed are derived over a stored Sent and are named here too, so a refusal never prints the member.
 */
export function purchaseOrderStatusLabel(status: PurchaseOrderStatusValue): string {
  return documentStatusPill('PurchaseOrderStatus', status).text;
}

/**
 * 🔒 Whether a gate may move an order from one status to another, or the
 * sentence saying why not (decision 1072). The order runs Draft, Approved,
 * Sent, Partly Received, Received and Closed, in that order and forward only.
 * Cancelled is reached from Draft, Approved or Sent, and Closed from Received
 * alone. A move to the status the order already stands at is no move and is
 * allowed, so a second part receipt or a re-sent revision passes, except that
 * a closed or cancelled order is already there and says so.
 */
export function purchaseOrderStatusMoveProblem(from: PurchaseOrderStatusValue, to: PurchaseOrderStatusValue): string | null {
  if (from === to) return from === 'Closed' || from === 'Cancelled' ? `This purchase order is already ${purchaseOrderStatusLabel(from)}.` : null;
  const order = PURCHASE_ORDER_STATUSES;
  const named = `This purchase order is ${purchaseOrderStatusLabel(from)}, so it does not move to ${purchaseOrderStatusLabel(to)}.`;
  if (from === 'Cancelled' || from === 'Closed') return named;
  if (to === 'Cancelled') return from === 'Draft' || from === 'Approved' || from === 'Sent' ? null : `${named} An order with a receipt against it is not canceled.`;
  if (to === 'Closed') return from === 'Received' ? null : `${named} Only a received order is closed.`;
  return order.indexOf(to) > order.indexOf(from) ? null : `${named} Its status runs Draft, Approved, Sent, Partly Received, Received and Closed, forward only.`;
}

/** The status a move lands at: the target where the move is forward, else the status the order already holds. */
export function purchaseOrderStatusForward(from: PurchaseOrderStatusValue, to: PurchaseOrderStatusValue): PurchaseOrderStatusValue {
  return purchaseOrderStatusMoveProblem(from, to) === null ? to : from;
}

/** The status a receipt or a ship confirmation writes, read off the receipt axis it leaves. */
export function purchaseOrderStatusOfReceipt(fulfillment: Exclude<PurchaseOrderFulfillmentStatusValue, 'Cancelled'>): PurchaseOrderStatusValue {
  return fulfillment === 'Received' ? 'Received' : fulfillment === 'PartiallyReceived' ? 'PartlyReceived' : 'Draft';
}

/**
 * The status an order written before the column existed seeds at, the
 * migration's own rule spelled once (decision 1072): the receipt axis first,
 * then the sent revision, then the approval.
 */
export function purchaseOrderStatusSeed(facts: { fulfillmentStatus: PurchaseOrderFulfillmentStatusValue; sentRevision: number | null; approvalStatus: ApprovalStatusValue }): PurchaseOrderStatusValue {
  if (facts.fulfillmentStatus === 'Cancelled') return 'Cancelled';
  if (facts.fulfillmentStatus === 'Received') return 'Received';
  if (facts.fulfillmentStatus === 'PartiallyReceived') return 'PartlyReceived';
  if (facts.sentRevision !== null) return 'Sent';
  if (facts.approvalStatus === 'Approved') return 'Approved';
  return 'Draft';
}

// ---- The footer ----

export type PurchaseOrderAction = 'approve' | 'reject' | 'resubmit' | 'send' | 'receive' | 'confirmShipment' | 'receiveUnsent' | 'createBill' | 'close' | 'void';

export interface PurchaseOrderActionFacts {
  fulfillmentMethod: PurchaseFulfillmentMethodValue;
  approvalStatus: ApprovalStatusValue;
  fulfillmentStatus: PurchaseOrderFulfillmentStatusValue;
  /** Whether this viewer can act on the pending step, which the gate answers. */
  mayApprove: boolean;
  /** Whether the vendor holds a copy (decision 0436); absent reads as not sent. */
  sent?: boolean;
  /** How many lines still carry a quantity no approved Bill has claimed; absent reads as none. */
  unbilledLines?: number;
  /** Decision 1072: the one status; absent on a fixture written before it, which offers no Close. */
  status?: PurchaseOrderStatusValue;
  /** Decision 1073: whether this viewer holds the administrator's override to receive an order nobody sent. */
  mayReceiveUnsent?: boolean;
}

/**
 * 🔒 Receiving, or confirming a drop shipment, is refused on an order the
 * vendor was never sent (T428; decision 1073). An administrator receives it
 * anyway with a reason, which the audit row keeps.
 */
export function purchaseOrderUnsentProblem(status: PurchaseOrderStatusValue): string | null {
  return status === 'Draft' || status === 'Approved' ? t('po.receive.unsentRefusal') : null;
}

/**
 * 🔒 Whether an order can be sent, or why not (T135; decision 0712). A void
 * order is not sent, and neither is one that has received material or had its
 * drop shipment confirmed: the vendor already delivered against it, and an
 * order past its first receipt is no longer edited, so there is no newer sheet
 * to send.
 */
export function purchaseOrderSendProblem(fulfillmentStatus: PurchaseOrderFulfillmentStatusValue): string | null {
  if (fulfillmentStatus === 'Cancelled') return 'a void purchase order is not sent';
  if (fulfillmentStatus !== 'NotStarted') return t('po.send.receivedRefusal');
  return null;
}

/** The footer's actions per status: the gate holds Send, Receive and the Ship Confirmation until the order is Approved or NotRequired. */
export function purchaseOrderActions(facts: PurchaseOrderActionFacts): PurchaseOrderAction[] {
  // 🔒 A Closed order is finished (decisions 1072, 3340): it is sent, received, billed and voided no more.
  if (facts.fulfillmentStatus === 'Cancelled' || facts.status === 'Closed') return [];
  const actions: PurchaseOrderAction[] = [];
  if (facts.approvalStatus === 'Pending') {
    if (facts.mayApprove) actions.push('approve', 'reject');
  } else if (facts.approvalStatus === 'Rejected') {
    actions.push('resubmit');
  } else {
    // 🔨 Send stands until anything is received or the drop shipment is confirmed, and never after (T135; decision 0712).
    if (purchaseOrderSendProblem(facts.fulfillmentStatus) === null) actions.push('send');
    // 🔒 Decision 1073: receiving waits for the send, and the control is not drawn until then (decision 1035's shape).
    const unsent = facts.status !== undefined && purchaseOrderUnsentProblem(facts.status) !== null;
    if (facts.fulfillmentMethod === 'Stock' && facts.fulfillmentStatus !== 'Received' && !unsent) actions.push('receive');
    if (facts.fulfillmentMethod === 'DropShip' && facts.fulfillmentStatus === 'NotStarted' && !unsent) actions.push('confirmShipment');
    // The administrator's override stands in their place, and names what it does.
    if (unsent && facts.mayReceiveUnsent) actions.push('receiveUnsent');
  }
  // 🔒 Create bill stands wherever the one rule a Bill is raised from clears the order (T136; decision 0711).
  if (purchaseOrderBillProblem({ ...facts, sent: facts.sent ?? false, unbilledLines: facts.unbilledLines ?? 0 }) === null) actions.push('createBill');
  // 🔒 Decision 1072: Closed is a person's act on a received order, the last forward move.
  if (facts.status === 'Received') actions.push('close');
  if (voidProblemOnReceipt(facts.fulfillmentStatus) === null) actions.push('void');
  return actions;
}

// ---- Raising a Bill from the order ----

/** What decides whether an order can be billed: its two axes, whether the vendor holds it, and whether anything is left to bill. */
export interface PurchaseOrderBillFacts {
  fulfillmentMethod: PurchaseFulfillmentMethodValue;
  approvalStatus: ApprovalStatusValue;
  fulfillmentStatus: PurchaseOrderFulfillmentStatusValue;
  /** Whether the vendor holds a copy: an emailed send or a Mark as sent wrote its audit row. */
  sent: boolean;
  /** How many of its lines still carry a quantity no approved Bill has claimed. */
  unbilledLines: number;
  /** The one linear status (decision 1072); a Closed order raises no bill (decision 3340). */
  status?: PurchaseOrderStatusValue | undefined;
}

/**
 * 🔒 Whether a Bill can be raised from this order, or the sentence saying why
 * not (T136, T140; decision 0711). One rule answers the Purchase Order's
 * Create bill, the Bill form's list of the vendor's open orders and the server's
 * build from the order, so the three cannot disagree.
 *
 * A Stock order is billed once the vendor holds it, received or not, because
 * billing first is supported (piece 24). A drop-ship order is billed once its
 * shipment is confirmed and never on send (piece 22).
 */
export function purchaseOrderBillProblem(facts: PurchaseOrderBillFacts): string | null {
  if (facts.fulfillmentStatus === 'Cancelled') return 'a canceled purchase order raises no bill';
  if (facts.status === 'Closed') return 'a closed purchase order raises no bill';
  if (facts.approvalStatus === 'Pending' || facts.approvalStatus === 'Rejected') return 'the approval gate still holds this purchase order, so nothing is billed against it yet';
  if (facts.fulfillmentMethod === 'DropShip' && facts.fulfillmentStatus !== 'Received') return 'a drop-ship order is billed once its shipment is confirmed, never on send';
  if (facts.fulfillmentMethod === 'Stock' && !facts.sent && facts.fulfillmentStatus === 'NotStarted') return 'the vendor has not been sent this order, so there is nothing of theirs to bill yet';
  if (facts.unbilledLines <= 0) return 'every line of this purchase order is already billed in full';
  return null;
}

// ---- The shapes the API and the pages exchange ----

export interface PurchaseOrderLineInput {
  /**
   * 🔒 The stored line this input is: present, that row is updated in place and
   * keeps its identity and its version counter; absent, a row is created; a
   * stored row no input names is deleted (decision 0445).
   */
  id?: string | null;
  itemId: string | null;
  description?: string | null;
  quantity: number;
  unitId?: string | null;
  /** Integer cents; null seeds from the awarded RFQ response or the Item's purchase price. */
  unitPriceCents?: number | null;
  /** 🔨 The line's own account override, the first rung of decision 0097's chain; absent, the vendor's purchase account answers and then the Item's. */
  accountId?: string | null;
  /** 🔨 What the purchase is taxed as (decisions 0086, 0435); absent, the Item's own category. A purchase line carries no tax amount. */
  taxCategoryId?: string | null;
}

export interface NewPurchaseOrderRequest {
  vendorCompanyId: string;
  fulfillmentMethod: PurchaseFulfillmentMethodValue;
  /** WEA's receiving site for Stock; ignored for DropShip, which takes the Sales Order's Ship To. */
  shipToSiteId?: string | null;
  salesOrderId?: string | null;
  dealId?: string | null;
  /** 🔒 The day the order is dated, `YYYY-MM-DD` (decision 0681); absent, today in the organisation's zone on a create and the stored day on an edit. */
  documentDate?: string | null;
  /** 🔒 The day the goods are wanted, `YYYY-MM-DD` (decision 0682); absent keeps what the order holds, null clears it. */
  deliveryDate?: string | null;
  /** 🔒 How the goods travel, a `ShipmentPreference` row (decision 0682); absent keeps what the order holds, null clears it. */
  shipmentPreferenceId?: string | null;
  /** 🔒 The notes to the vendor Region 7 prints (decision 0683); absent keeps what the order holds. */
  customerNotes?: string | null;
  /** 🔒 Decision 9600: the reason the one-time tax exempt form claims, Resale or Manufacturing; null is None, and absent keeps what the order holds. */
  exemptionReason?: string | null;
  lines: PurchaseOrderLineInput[];
  /** 🔒 The group dividers among the lines, each at its place in the one reading order (decision 0650); absent, none. */
  dividers?: DividerPlace[];
}

export interface ReceiveRequest {
  lines: ReceiptLine[];
  /** A calendar date, YYYY-MM-DD, in the organization's zone; today when blank. */
  receivedOn?: string | null;
  note?: string | null;
  /** Decision 1073: why an administrator receives an order the vendor was never sent; refused from anybody else. */
  overrideReason?: string | null;
}

/** One confirmation shape for a drop-ship order and for a Sales Order shipment; `originSiteId` matters to the second alone. */
export interface ShipConfirmationRequest {
  shippedOn: string;
  carrier?: string | null;
  trackingNumber: string;
  originSiteId?: string | null;
  /** Decision 1073: the same override on a drop-ship order confirmed before it was sent; a Sales Order shipment ignores it. */
  overrideReason?: string | null;
}

export interface PurchaseOrderLineAnswer {
  id: string;
  lineNumber: number;
  itemId: string | null;
  sku: string | null;
  itemName: string | null;
  description: string;
  quantity: number;
  quantityConsumed: number;
  unitId: string;
  unitName: string;
  unitPriceCents: number | null;
  amountCents: number;
  /** The account and the category the line was written with, so Edit reopens on them rather than re-defaulting (decision 0435). */
  accountId: string;
  accountLabel: string;
  taxCategoryId: string;
  taxCategoryName: string;
  vendorPartNumber: string | null;
  manufacturerPartNumber: string | null;
  manufacturerReferenceName: string | null;
  /** The note the order's own page reads under the line, naming each part it shows (piece 22). */
  referenceNote: string | null;
}

export interface PurchaseOrderAnswer {
  /** 🔒 Why the document rests where it does, and a Draft's one primary action (T131, T223; decision 0713); absent from an older reply. */
  approval?: ApprovalView;
  /** 🔒 The group dividers among the lines, each at its place in the one reading order (decision 0650); absent from an older reply. */
  dividers?: DividerRow[];
  id: string;
  purchaseOrderNumber: string;
  /** How many times a sent order has been revised; zero until the first revise (decision 0485). */
  revision: number;
  /** The number as it prints: the base number alone at revision zero, base-and-revision after that. */
  displayNumber: string;
  /** What the Edit control does here: edit in place, revise as the next number, or nothing. */
  editVerdict: PurchaseOrderEditVerdict;
  /** Why the edit is refused, for the page to say instead of drawing a control; null where it is offered. */
  editFrozenReason: string | null;
  /** The revision the edit would become, for the control to name its own consequence; null unless the verdict is revise. */
  nextRevision: string | null;
  /** The revision the vendor was last sent; null where the order has never gone out (decision 0485). */
  sentRevision: number | null;
  vendorCompanyId: string;
  vendorName: string;
  shipToSiteId: string;
  shipToLabel: string;
  fulfillmentMethod: PurchaseFulfillmentMethodValue;
  origin: PurchaseOrderOriginValue;
  approvalStatus: ApprovalStatusValue;
  approvedByName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  escalationReason: string | null;
  fulfillmentStatus: PurchaseOrderFulfillmentStatusValue;
  /** Decision 1072: the one linear status the page's pill and the list's column read. */
  status: PurchaseOrderStatusValue;
  /** 🔒 The status as a person reads it: Sending or Delivery Failed over a Sent whose send the host has not confirmed (decision 1225); absent from an older reply. */
  displayedStatus?: string;
  /** 🔒 The newest send's Delivered panel (decision 1226); null where nothing was mailed; absent from an older reply. */
  delivery?: DocumentDeliveryView | null;
  /** 🔒 The date and the threshold the send window warns on, on an order never sent (decision 1227); absent from an older reply. */
  staleSend?: { documentDate: string; thresholdDays: number } | null;
  minimumMarginCleared: boolean;
  sourceRFQId: string | null;
  sourceRfqNumber: string | null;
  dealId: string | null;
  dealName: string | null;
  salesOrderId: string | null;
  salesOrderNumber: string | null;
  createdAt: string;
  /** 🔒 The day the order is dated, `YYYY-MM-DD` (decision 0681). */
  documentDate: string;
  /** 🔒 The day the goods are wanted, printed as Deliver by (decision 0682); null where none is named. */
  deliveryDate: string | null;
  /** 🔒 How the goods travel (decision 0682); null where the order names none. */
  shipmentPreferenceId: string | null;
  /** 🔒 The notes to the vendor Region 7 prints (decision 0683); null where none are typed. */
  customerNotes: string | null;
  /** 🔒 Decision 9600: the reason the order's one-time tax exempt form claims; null is None. Absent from an older reply. */
  exemptionReason?: string | null;
  /** 🔒 Decision 9600: the File the filled form is kept as, once a send has written it. Absent from an older reply. */
  exemptionFormFileId?: string | null;
  createdByName: string;
  /** Region 2 on the printed sheet: the issuing organization's own name (decision 0409). */
  organizationName: string;
  /**
   * 🔒 The one projection the page and the PDF both render (piece 27; decision
   * 0409). It is built on the server and never in the browser, so the screen and
   * the printed sheet cannot drift apart.
   */
  printDocument: PrintDocument;
  /**
   * 🔒 The layout the document prints on, resolved once beside the projection
   * (decision 0567): view mode positions these region rows, so a region nudged
   * in the editor moves on the page. Absent where no template is laid out, and
   * the page then falls back to the reading order it drew before.
   */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet both views read, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /** Whether the order has gone to the vendor; an unsent order is the only one that edits (found while building, 2026-09-15). */
  sent: boolean;
  /** Decision 1073: goods stand against an order the vendor was never sent, so the page reads a warning; the receipt is kept. */
  receivedUnsent: boolean;
  totalCents: number;
  currencyCode: string;
  lines: PurchaseOrderLineAnswer[];
  actions: PurchaseOrderAction[];
  /** Whether the margin working's control is drawn for this viewer; the route refuses everybody else (decision 0375). */
  offersMarginWorking: boolean;
  /** `originLabel` is the site the stock left, empty on a shipment that moved none (decision 0502). */
  shipments: { id: string; shipmentNumber: string; status: string; shippedDate: string | null; trackingNumber: string | null; originLabel?: string | null }[];
  /**
   * 🔒 Every sheet the vendor has been sent, newest first (decision 0490).
   * Empty until the first send, because a version is written by Send and by
   * nothing else — a draft has no copy because nobody has been given one.
   */
  versions: { id: string; versionNumber: number; heading: string; sentAt: string; sentByName: string }[];
}

// ---- The revision (decision 0485) ----

/**
 * What an edit does to an order, by what has happened to it. A Purchase Order
 * that has gone nowhere edits in place; one whose current revision the vendor
 * already holds becomes the next revision, the way a Quote does; one that has
 * received material or been cancelled is frozen, because the receipt stands in
 * the stock ledger and a cancelled order is not brought back by editing it.
 *
 * 🔨 The revision counts sheets the vendor holds, never edits somebody made.
 * An order revised and not yet re-sent edits in place: `sentRevision` is what
 * says which sheet is out there, and until the revision goes out, the number
 * on it would otherwise climb past anything the vendor has ever seen.
 *
 * 🔨 The revision is the ONLY thing a sent order's edit does differently. The
 * lines are rewritten under the same line-identity rules, and the approval gate
 * sees the same material changes, because a vendor holding the old sheet is a
 * reason to number the new one, not a reason to check it less.
 */
export type PurchaseOrderEditVerdict = 'edit' | 'revise' | 'frozen';

export interface PurchaseOrderEditFacts {
  /** The one linear status (decision 1072); Closed freezes the edit whatever the receipt axis reads (decision 3701). */
  status: PurchaseOrderStatusValue;
  fulfillmentStatus: PurchaseOrderFulfillmentStatusValue;
  revision: number;
  /** The revision the vendor was last sent; null where the order has never gone out. */
  sentRevision: number | null;
}

export function purchaseOrderEditVerdict(order: PurchaseOrderEditFacts): PurchaseOrderEditVerdict {
  // 🔒 A Closed order is finished (decision 3701): one imported Closed with nothing received is frozen as a received one is.
  if (order.status === 'Closed' || order.fulfillmentStatus !== 'NotStarted') return 'frozen';
  if (order.sentRevision === null) return 'edit';
  return order.sentRevision >= order.revision ? 'revise' : 'edit';
}

/** Why a frozen order refuses the edit, naming what to do instead (decision 0458); a Closed one names its status (decision 3701). */
export function purchaseOrderFrozenReason(order: Pick<PurchaseOrderEditFacts, 'status' | 'fulfillmentStatus'>): string {
  if (order.fulfillmentStatus === 'Cancelled') return 'a canceled purchase order is not edited; raise a new one';
  if (order.status === 'Closed') return t('po.edit.closedRefusal');
  return 'a purchase order that has received material is not edited; raise a new one for what is still to come';
}

/** The writes a revise makes: the next revision, the base number never moving. */
export function purchaseOrderReviseWrites(order: { revision: number }): { revision: number } {
  return { revision: order.revision + 1 };
}

/**
 * 🔒 The line the printed sheet carries when it is a revision, so a vendor
 * holding two sheets with the same base number can tell which is current
 * (product owner, 2026-09-17). Revision zero prints no line at all: every
 * order would otherwise carry a notice saying it is not a revision.
 */
export function purchaseOrderRevisionNotice(revision: number): string | null {
  if (revision <= 0) return null;
  const previous = String(revision - 1).padStart(2, '0');
  return `Revised purchase order · this revision replaces revision ${previous}, which is withdrawn`;
}

export interface PurchaseOrderRow {
  id: string;
  purchaseOrderNumber: string;
  /** The number as it prints, carrying the revision where the order has one (decision 0485). */
  displayNumber: string;
  vendorName: string;
  fulfillmentMethod: PurchaseFulfillmentMethodValue;
  approvalStatus: ApprovalStatusValue;
  fulfillmentStatus: PurchaseOrderFulfillmentStatusValue;
  status: PurchaseOrderStatusValue;
  totalCents: number;
  createdAt: string;
}

/**
 * The mail a Purchase Order goes to its vendor under (piece 22; decision 0832, amended): the greeting, the order, the
 * buyer's note and the ask, with the sign-off apart. 🔒 The sign-off yields to the sender's signature where one is set,
 * so the vendor reads one closing, never the buyer's name twice.
 */
export function purchaseOrderMail(facts: { purchaseOrderNumber: string; organizationName: string; contactName: string | null; lineCount: number; message?: string | null; dropShip: boolean; orderedBy: string }): { subject: string; body: string; closing: string } {
  return {
    subject: `[${facts.purchaseOrderNumber}] Purchase order from ${facts.organizationName}`,
    body: [
      facts.contactName ? `${facts.contactName},` : 'Hello,',
      '',
      `Please find attached purchase order ${facts.purchaseOrderNumber} from ${facts.organizationName}, ${counted(facts.lineCount, 'line')}.`,
      ...(facts.message?.trim() ? ['', facts.message.trim()] : []),
      '',
      facts.dropShip ? 'This order ships directly to our customer at the address on the order; please send the shipping confirmation with the tracking reference.' : 'Please confirm the order and the delivery date, quoting the purchase order number.',
    ].join('\n'),
    closing: [facts.orderedBy, facts.organizationName].filter((line) => line.trim() !== '').join('\n'),
  };
}
